import React, { useEffect, useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography,
  Box
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ReduxStore, IPatient } from '../../../../constants/types';
import { ChartData } from '../types';

interface DiagnosisTableProps {
  data: ChartData;
  title: string;
}

const DiagnosisTable: React.FC<DiagnosisTableProps> = ({ data, title }) => {
  const [diagnosisData, setDiagnosisData] = useState<any[]>([]);
  
  // Get patient data from Redux store
  const patientsData = useSelector((state: ReduxStore) => {
    const currentInvestigation = state.investigations.currentInvestigation;
    if (!currentInvestigation) return [];
    return state.patients.data?.[currentInvestigation.uuid] || [];
  });

  useEffect(() => {
    if (!data || !data.categories || !data.patientIdsByCategory) {
      return;
    }

    // Extract the categories (diagnosis names) and patient IDs by diagnosis
    const diagnosisNames = data.categories;
    const patientIdsByCategory = data.patientIdsByCategory;

    // Create a mapping of patient IDs to their gender
    const patientGenderMap = new Map<number, string>();
    patientsData.forEach((patient: IPatient) => {
      if (patient && patient.id && patient.personalData && patient.personalData.sex) {
        patientGenderMap.set(patient.id, patient.personalData.sex);
      }
    });

    // Process the diagnosis data
    const tableData = diagnosisNames.map((diagnosisName, index) => {
      // SHER-880: the row's patients used to be reached by .find()ing over diagnosis codes
      // with a predicate that compared data.categories[index] against diagnosisName -- the
      // same value, so it was always true, `code` was never read, and .find() returned the
      // first key every time. Every row counted the alphabetically first diagnosis's
      // patients. The ids now arrive aligned with `categories`, so the row's own patients
      // are at the row's own index and there is no key to resolve.
      const patientIds = patientIdsByCategory[index] || [];

      let femaleCount = 0;
      let maleCount = 0;
      let totalCount = data.series[2]?.data[index] || 0; // Total is the third series

      patientIds.forEach(patientId => {
        const gender = patientGenderMap.get(patientId);
        if (gender === 'Female') {
          femaleCount++;
        } else if (gender === 'Male') {
          maleCount++;
        }
      });

      return {
        diagnosis: diagnosisName,
        female: femaleCount,
        male: maleCount,
        total: totalCount
      };
    });

    setDiagnosisData(tableData);
  }, [data, patientsData]);

  if (!data || !data.categories || !data.patientIdsByCategory || diagnosisData.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="subtitle1">No diagnosis data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Diagnosis</strong></TableCell>
              <TableCell align="right"><strong>Female</strong></TableCell>
              <TableCell align="right"><strong>Male</strong></TableCell>
              <TableCell align="right"><strong>Total</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diagnosisData.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.diagnosis}</TableCell>
                <TableCell align="right">{row.female}</TableCell>
                <TableCell align="right">{row.male}</TableCell>
                <TableCell align="right">{row.total}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DiagnosisTable; 